import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, formatDistanceToNow, subHours, subDays } from 'date-fns';
import { useWebSocket } from '../contexts/WebSocketContext';
import axiosInstance from '../utils/axios';
import {
  ClockIcon,
  UserGroupIcon,
  CommandLineIcon,
  BoltIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarIcon,
  SignalIcon,
  MapPinIcon,
  SignalSlashIcon,
  UserIcon,
  PlayIcon,
  StopIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  EyeIcon,
  WifiIcon,
  ServerIcon,
  ChartBarIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathRoundedSquareIcon,
  ComputerDesktopIcon,
  ArrowDownTrayIcon,
  CloudArrowDownIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

// Action Status Badge Component
const ActionStatusBadge = ({ status, retryCount }) => {
  const config = {
    acknowledged: {
      icon: CheckCircleIcon,
      bg: 'bg-green-100',
      text: 'text-green-700',
      label: 'Success'
    },
    pending: {
      icon: ClockIcon,
      bg: 'bg-yellow-100',
      text: 'text-yellow-700',
      label: 'Pending'
    },
    failed: {
      icon: XCircleIcon,
      bg: 'bg-red-100',
      text: 'text-red-700',
      label: 'Failed'
    },
    timeout: {
      icon: ExclamationTriangleIcon,
      bg: 'bg-orange-100',
      text: 'text-orange-700',
      label: 'Timeout'
    },
    legacy: {
      icon: ClockIcon,
      bg: 'bg-gray-100',
      text: 'text-gray-600',
      label: 'Legacy'
    }
  };

  const { icon: Icon, bg, text, label } = config[status] || config.pending;

  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${bg} ${text} text-xs`}>
      <Icon className="w-3 h-3" />
      <span>{label}</span>
      {retryCount > 0 && (
        <>
          <ArrowPathIcon className="w-3 h-3 ml-1" />
          <span>{retryCount}</span>
        </>
      )}
    </div>
  );
};

const EventIcon = ({ type, actionType }) => {
  const icons = {
    session_start: SignalIcon,
    session_end: SignalSlashIcon,
    subscriber_joined: ArrowRightOnRectangleIcon,
    subscriber_left: ArrowLeftOnRectangleIcon,
    stream_started: PlayIcon,
    stream_stopped: StopIcon,
    action: CommandLineIcon,
    update_api: BoltIcon,
    reboot_command: ArrowPathIcon,
    terminal_session: ComputerDesktopIcon,
    connection_refresh: ArrowPathIcon,
    session_refresh: ArrowPathRoundedSquareIcon,
    update_initiated: ArrowDownTrayIcon,
    update_transfer_started: CloudArrowDownIcon,
    update_acknowledgment: CheckCircleIcon,
    update_progress: ArrowPathIcon,
    update_completed: CheckBadgeIcon,
    update_device_online: SignalIcon
  };
  
  // For action events, check the actionType
  if (type === 'action' && actionType === 'reboot_command') {
    const RebootIcon = ArrowPathIcon;
    return <RebootIcon className="w-4 h-4 text-red-600" />;
  }
  
  if (type === 'action' && actionType === 'connection_refresh') {
    const RefreshIcon = ArrowPathIcon;
    return <RefreshIcon className="w-4 h-4 text-yellow-600" />;
  }
  
  if (type === 'action' && actionType === 'session_refresh') {
    const SessionRefreshIcon = ArrowPathRoundedSquareIcon;
    return <SessionRefreshIcon className="w-4 h-4 text-blue-600" />;
  }
  
  if (type === 'action' && actionType === 'terminal_session') {
    const TerminalIcon = ComputerDesktopIcon;
    return <TerminalIcon className="w-4 h-4 text-blue-600" />;
  }
  
  // Software update icons
  if (type === 'action' && actionType === 'update_initiated') {
    return <ArrowDownTrayIcon className="w-4 h-4 text-purple-600" />;
  }
  
  if (type === 'action' && actionType === 'update_transfer_started') {
    return <CloudArrowDownIcon className="w-4 h-4 text-purple-600" />;
  }
  
  if (type === 'action' && actionType === 'update_acknowledgment') {
    return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
  }
  
  if (type === 'action' && actionType === 'update_progress') {
    return <ArrowPathIcon className="w-4 h-4 text-blue-600" />;
  }
  
  if (type === 'action' && actionType === 'update_completed') {
    return <CheckBadgeIcon className="w-4 h-4 text-green-600" />;
  }
  
  if (type === 'action' && actionType === 'update_device_online') {
    return <SignalIcon className="w-4 h-4 text-green-600" />;
  }
  
  const Icon = icons[actionType] || icons[type] || CommandLineIcon;
  return <Icon className="w-4 h-4" />;
};

const EventTypeLabel = ({ type, actionType, payload }) => {
  const labels = {
    session_start: 'Session Started',
    session_end: 'Session Ended',
    subscriber_joined: 'User Joined',
    subscriber_left: 'User Left',
    stream_started: 'Stream Started',
    stream_stopped: 'Stream Stopped',
    action: 'Action',
    update_api: 'API Update',
    reboot_command: 'Device Reboot',
    terminal_session: 'Terminal Session',
    connection_refresh: 'Connection Refresh',
    session_refresh: 'Session Refresh',
    update_initiated: 'Update Started',
    update_transfer_started: 'Transfer Started',
    update_acknowledgment: 'Update Acknowledged',
    update_progress: 'Update Progress',
    update_completed: 'Update Completed',
    update_device_online: 'Device Online'
  };
  
  // For action events, check the actionType
  if (type === 'action' && actionType === 'reboot_command') {
    return <span className="text-red-600 font-semibold">Device Reboot</span>;
  }
  
  if (type === 'action' && actionType === 'connection_refresh') {
    return <span className="text-yellow-600 font-semibold">Connection Refresh</span>;
  }
  
  if (type === 'action' && actionType === 'session_refresh') {
    return <span className="text-blue-600 font-semibold">Session Refresh (Health)</span>;
  }
  
  if (type === 'action' && actionType === 'terminal_session') {
    const action = payload?.action || 'unknown';
    const terminalType = payload?.terminalType || 'Terminal';
    if (action === 'opened') {
      return <span className="text-blue-600 font-semibold">{terminalType} Opened</span>;
    } else if (action === 'closed') {
      return <span className="text-gray-600 font-semibold">{terminalType} Closed</span>;
    }
    return <span className="text-blue-600 font-semibold">Terminal Session</span>;
  }
  
  // Software update labels
  if (type === 'action' && actionType === 'update_initiated') {
    return <span className="text-purple-600 font-semibold">Update Started</span>;
  }
  
  if (type === 'action' && actionType === 'update_transfer_started') {
    return <span className="text-purple-600 font-semibold">Transfer Started</span>;
  }
  
  if (type === 'action' && actionType === 'update_acknowledgment') {
    return <span className="text-green-600 font-semibold">Update Acknowledged</span>;
  }
  
  if (type === 'action' && actionType === 'update_progress') {
    return <span className="text-blue-600 font-semibold">Update Progress</span>;
  }
  
  if (type === 'action' && actionType === 'update_completed') {
    return <span className="text-green-600 font-semibold">Update Completed</span>;
  }
  
  if (type === 'action' && actionType === 'update_device_online') {
    return <span className="text-green-600 font-semibold">Device Online</span>;
  }
  
  return <span>{labels[actionType] || labels[type] || type}</span>;
};

const SessionCard = ({ session, onViewDetails }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Session Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {session.isActive ? (
              <SignalIcon className="w-5 h-5 text-green-600" />
            ) : (
              <SignalSlashIcon className="w-5 h-5 text-gray-400" />
            )}
            <div>
              <p className="font-medium text-gray-900">
                Session {session.sessionId.slice(-8)}
                {session.previousSessionId && (
                  <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    Health Refresh
                  </span>
                )}
              </p>
              <p className="text-sm text-gray-500">
                {format(new Date(session.connectedAt), 'MMM dd, HH:mm:ss')}
                {session.previousSessionId && (
                  <span className="ml-2 text-xs text-blue-600">
                    (from ...{session.previousSessionId.slice(-8)})
                  </span>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <ClockIcon className="w-4 h-4" />
                {session.durationFormatted}
              </div>
              <div className="flex items-center gap-1">
                <UserGroupIcon className="w-4 h-4" />
                {session.uniqueUserCount} user{session.uniqueUserCount !== 1 ? 's' : ''}
              </div>
              <div className="flex items-center gap-1">
                <CommandLineIcon className="w-4 h-4" />
                {session.totalActions} action{session.totalActions !== 1 ? 's' : ''}
              </div>
            </div>
            
            {isExpanded ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-gray-200"
          >
            <div className="p-4 bg-gray-50 space-y-4">
              {/* Session Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <p className="text-sm font-medium text-gray-600 mb-1">Connection Info</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <WifiIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">{session.ip || 'Unknown IP'}</span>
                    </div>
                    {session.location?.formatted && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPinIcon className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-700">{session.location.formatted}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <ServerIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700 truncate">{session.userAgent || 'Unknown Agent'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <p className="text-sm font-medium text-gray-600 mb-1">Activity Summary</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Subscribers:</span>
                      <span className="ml-1 font-medium">{session.totalSubscribers}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Streams:</span>
                      <span className="ml-1 font-medium">{session.totalStreams}</span>
                    </div>
                    {Object.entries(session.actionTypes).map(([type, count]) => (
                      <div key={type}>
                        <span className="text-gray-500">{type}:</span>
                        <span className="ml-1 font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <p className="text-sm font-medium text-gray-600 mb-1">Timing</p>
                  <div className="space-y-1 text-sm">
                    <div>
                      <span className="text-gray-500">Started:</span>
                      <span className="ml-1">{format(new Date(session.connectedAt), 'HH:mm:ss')}</span>
                    </div>
                    {session.disconnectedAt && (
                      <div>
                        <span className="text-gray-500">Ended:</span>
                        <span className="ml-1">{format(new Date(session.disconnectedAt), 'HH:mm:ss')}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Duration:</span>
                      <span className="ml-1 font-medium">{session.durationFormatted}</span>
                    </div>
                    {session.disconnectionReason && (
                      <div>
                        <span className="text-gray-500">Reason:</span>
                        <span className="ml-1 text-red-600 text-xs">{formatDisconnectionReason(session.disconnectionReason)}</span>
                      </div>
                    )}
                    {session.refreshReason && (
                      <div>
                        <span className="text-gray-500">Refresh:</span>
                        <span className="ml-1 text-blue-600 text-xs">{session.refreshReason.replace(/_/g, ' ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Subscribers */}
              {session.subscribers.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Subscribers</h4>
                  <div className="space-y-2">
                    {session.subscribers.map((sub, idx) => (
                      <div key={idx} className="bg-white rounded p-2 border border-gray-200 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <UserIcon className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium">{sub.name || 'Unknown User'}</p>
                            <p className="text-xs text-gray-500">{sub.role} • Topics: {sub.topics.join(', ')}</p>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {format(new Date(sub.connectedAt), 'HH:mm:ss')}
                          {sub.disconnectedAt && ` - ${format(new Date(sub.disconnectedAt), 'HH:mm:ss')}`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Actions */}
              {session.actions.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Actions</h4>
                  <div className="space-y-2">
                    {session.actions.slice(0, 5).map((action, idx) => (
                      <div key={idx} className={`rounded p-2 border ${
                        action.type === 'reboot_command' 
                          ? 'bg-red-50 border-red-200'
                          : action.type === 'connection_refresh'
                          ? 'bg-yellow-50 border-yellow-200'
                          : action.type === 'session_refresh'
                          ? 'bg-blue-50 border-blue-200'
                          : action.type === 'terminal_session'
                          ? 'bg-blue-50 border-blue-200'
                          : ['update_initiated', 'update_transfer_started', 'update_acknowledgment', 'update_progress', 'update_completed', 'update_device_online'].includes(action.type)
                          ? 'bg-purple-50 border-purple-200'
                          : 'bg-white border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {action.type === 'reboot_command' ? (
                              <ArrowPathIcon className="w-4 h-4 text-red-600" />
                            ) : action.type === 'connection_refresh' ? (
                              <ArrowPathIcon className="w-4 h-4 text-yellow-600" />
                            ) : action.type === 'session_refresh' ? (
                              <ArrowPathRoundedSquareIcon className="w-4 h-4 text-blue-600" />
                            ) : action.type === 'terminal_session' ? (
                              <ComputerDesktopIcon className="w-4 h-4 text-blue-600" />
                            ) : action.type === 'update_initiated' ? (
                              <ArrowDownTrayIcon className="w-4 h-4 text-purple-600" />
                            ) : action.type === 'update_transfer_started' ? (
                              <CloudArrowDownIcon className="w-4 h-4 text-purple-600" />
                            ) : action.type === 'update_acknowledgment' ? (
                              <CheckCircleIcon className="w-4 h-4 text-green-600" />
                            ) : action.type === 'update_progress' ? (
                              <ArrowPathIcon className="w-4 h-4 text-blue-600" />
                            ) : action.type === 'update_completed' ? (
                              <CheckBadgeIcon className="w-4 h-4 text-green-600" />
                            ) : action.type === 'update_device_online' ? (
                              <SignalIcon className="w-4 h-4 text-green-600" />
                            ) : (
                              <CommandLineIcon className="w-4 h-4 text-gray-400" />
                            )}
                            <span className={`text-sm font-medium ${
                              action.type === 'reboot_command' ? 'text-red-700' : 
                              action.type === 'connection_refresh' ? 'text-yellow-700' :
                              action.type === 'session_refresh' ? 'text-blue-700' :
                              action.type === 'terminal_session' ? 'text-blue-700' :
                              ['update_initiated', 'update_transfer_started'].includes(action.type) ? 'text-purple-700' :
                              ['update_acknowledgment', 'update_completed', 'update_device_online'].includes(action.type) ? 'text-green-700' :
                              action.type === 'update_progress' ? 'text-blue-700' : ''
                            }`}>
                              {action.type === 'reboot_command' ? 'Device Reboot' : 
                               action.type === 'connection_refresh' ? 'Connection Refresh' :
                               action.type === 'session_refresh' ? 'Session Health Refresh' :
                               action.type === 'terminal_session' ? 
                                 (action.payload?.action === 'opened' ? 
                                   `${action.payload?.terminalType || 'Terminal'} Opened` : 
                                   `${action.payload?.terminalType || 'Terminal'} Closed`) :
                               action.type === 'update_initiated' ? 'Update Started' :
                               action.type === 'update_transfer_started' ? 'Transfer Started' :
                               action.type === 'update_acknowledgment' ? 'Update Acknowledged' :
                               action.type === 'update_progress' ? `Progress: ${action.payload?.progress || 0}%` :
                               action.type === 'update_completed' ? 'Update Completed' :
                               action.type === 'update_device_online' ? 'Device Online' :
                               action.type}
                            </span>
                            {action.name && (
                              <span className="text-xs text-gray-500">by {action.name}</span>
                            )}
                            {/* Status Badge */}
                            {action.commandId && (
                              <ActionStatusBadge 
                                status={action.ackStatus} 
                                retryCount={action.retryCount}
                              />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {action.responseTime && (
                              <span className="text-xs text-green-600">
                                {action.responseTime}ms
                              </span>
                            )}
                            <span className="text-xs text-gray-500">
                              {format(new Date(action.timestamp), 'HH:mm:ss')}
                            </span>
                          </div>
                        </div>
                        {action.topic && (
                          <p className="text-xs text-gray-500 mt-1">Topic: {action.topic}</p>
                        )}
                        {action.payload && action.type === 'update_api' && (
                          <div className="mt-1 text-xs bg-gray-50 rounded p-1">
                            {action.payload.map && action.payload.map((update, idx) => (
                              <div key={idx} className="text-gray-600">
                                Changed <span className="font-medium">{update.path}</span> to{' '}
                                <span className="font-medium text-blue-600">{update.value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Retry/Failure Information */}
                        {action.retryCount > 0 && (
                          <div className="mt-1 text-xs bg-yellow-50 rounded p-1 text-yellow-700">
                            <ArrowPathIcon className="w-3 h-3 inline mr-1" />
                            Retried {action.retryCount} time{action.retryCount > 1 ? 's' : ''}
                            {action.lastRetryAt && (
                              <span className="ml-1">
                                • Last retry: {format(new Date(action.lastRetryAt), 'HH:mm:ss')}
                              </span>
                            )}
                          </div>
                        )}
                        {action.errorMessage && (
                          <div className="mt-1 text-xs bg-red-50 rounded p-1 text-red-700">
                            <XCircleIcon className="w-3 h-3 inline mr-1" />
                            Error: {action.errorMessage}
                          </div>
                        )}
                      </div>
                    ))}
                    {session.actions.length > 5 && (
                      <p className="text-xs text-gray-500 text-center">
                        And {session.actions.length - 5} more actions...
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* View Details Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDetails(session);
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <EyeIcon className="w-4 h-4" />
                View Full Timeline
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SessionTimeline = ({ session, timeline, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Session Timeline</h2>
              <p className="text-sm text-gray-500 mt-1">
                Session ID: {session.sessionId}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

            {/* Timeline events */}
            <div className="space-y-4">
              {timeline.map((event, idx) => (
                <div key={idx} className="relative flex items-start">
                  <div className={`
                    absolute left-4 w-4 h-4 rounded-full border-2 bg-white
                    ${event.type === 'session_start' ? 'border-green-500' : ''}
                    ${event.type === 'session_end' ? 'border-red-500' : ''}
                    ${event.type === 'action' ? 'border-blue-500' : ''}
                    ${event.type.includes('subscriber') ? 'border-purple-500' : ''}
                    ${event.type.includes('stream') ? 'border-yellow-500' : ''}
                    ${!['session_start', 'session_end', 'action'].includes(event.type) && !event.type.includes('subscriber') && !event.type.includes('stream') ? 'border-gray-400' : ''}
                  `}></div>
                  
                  <div className="ml-12 flex-1">
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <EventIcon type={event.type} actionType={event.data?.actionType} />
                          <span className="font-medium text-sm">
                            <EventTypeLabel type={event.type} actionType={event.data?.actionType} payload={event.data?.payload} />
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {format(new Date(event.timestamp), 'HH:mm:ss.SSS')}
                        </span>
                      </div>
                      
                      {event.data && (
                        <div className="mt-2 text-sm text-gray-600">
                          {event.data.name && (
                            <p>User: {event.data.name} ({event.data.role})</p>
                          )}
                          {event.data.topic && (
                            <p>Topic: {event.data.topic}</p>
                          )}
                          {event.data.topics && (
                            <p>Topics: {event.data.topics.join(', ')}</p>
                          )}
                          {event.data.actionType && (
                            <p>Type: {event.data.actionType}</p>
                          )}
                          {event.data.responseTime && (
                            <p>Response Time: {event.data.responseTime}ms</p>
                          )}
                          {event.data.ip && (
                            <p>IP: {event.data.ip}</p>
                          )}
                          {event.data.location?.formatted && (
                            <p>Location: {event.data.location.formatted}</p>
                          )}
                          {/* Show IP for subscriber events */}
                          {event.type === 'subscriber_joined' && event.data.ip && (
                            <div className="mt-1 text-xs text-gray-500">
                              <p>Connected from: {event.data.ip}</p>
                            </div>
                          )}
                          {event.type === 'action' && event.data.actionType === 'terminal_session' && (
                            <div className={`mt-2 rounded p-2 border ${
                              event.data.payload?.action === 'opened' 
                                ? 'bg-blue-50 border-blue-200' 
                                : 'bg-gray-50 border-gray-200'
                            }`}>
                              <p className={`font-medium text-xs mb-1 ${
                                event.data.payload?.action === 'opened' 
                                  ? 'text-blue-700' 
                                  : 'text-gray-700'
                              }`}>
                                {event.data.payload?.action === 'opened' ? '🖥️ ' : '🔚 '}
                                {event.data.payload?.terminalType || 'Terminal'} 
                                {event.data.payload?.action === 'opened' ? ' Opened' : ' Closed'}
                              </p>
                              <div className="text-xs text-gray-600">
                                <p>By: {event.data.name || event.data.userId}</p>
                                {event.data.payload?.sessionId && (
                                  <p className="font-mono text-xs">Session: {event.data.payload.sessionId.slice(-8)}</p>
                                )}
                                {event.data.payload?.cols && event.data.payload?.rows && (
                                  <p>Size: {event.data.payload.cols}x{event.data.payload.rows}</p>
                                )}
                                {event.data.payload?.durationFormatted && (
                                  <p>Duration: {event.data.payload.durationFormatted}</p>
                                )}
                                {event.data.responseTime && (
                                  <p>Response Time: {event.data.responseTime}ms</p>
                                )}
                              </div>
                            </div>
                          )}
                          {event.type === 'action' && event.data.actionType === 'reboot_command' && (
                            <div className="mt-2 bg-red-50 rounded p-2 border border-red-200">
                              <p className="font-medium text-xs text-red-700 mb-1">⚠️ Device Reboot Initiated</p>
                              <div className="text-xs text-red-600">
                                <p>Initiated by: {event.data.name || event.data.userId}</p>
                                {event.data.payload?.delay && (
                                  <p>Delay: {event.data.payload.delay} seconds</p>
                                )}
                                {event.data.payload?.reason && (
                                  <p>Reason: {event.data.payload.reason}</p>
                                )}
                                {event.data.responseTime && (
                                  <p>Response Time: {event.data.responseTime}ms</p>
                                )}
                              </div>
                            </div>
                          )}
                          {event.type === 'action' && event.data.actionType === 'connection_refresh' && (
                            <div className="mt-2 bg-yellow-50 rounded p-2 border border-yellow-200">
                              <p className="font-medium text-xs text-yellow-700 mb-1">🔄 Connection Refresh</p>
                              <div className="text-xs text-yellow-700">
                                <p>Initiated by: {event.data.name || event.data.userId}</p>
                                {event.data.payload?.reason && (
                                  <p>Reason: {event.data.payload.reason}</p>
                                )}
                                {event.data.payload?.requestedBy && (
                                  <p>Email: {event.data.payload.requestedBy}</p>
                                )}
                                {event.data.responseTime && (
                                  <p>Response Time: {event.data.responseTime}ms</p>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {event.type === 'action' && event.data.actionType === 'session_refresh' && (
                            <div className="mt-2 bg-blue-50 rounded p-2 border border-blue-200">
                              <p className="font-medium text-xs text-blue-700 mb-1">♻️ Session Health Refresh</p>
                              <div className="text-xs text-blue-700">
                                <p>Type: Automatic health-based refresh</p>
                                {event.data.payload?.reason && (
                                  <p>Reason: {event.data.payload.reason.replace(/_/g, ' ')}</p>
                                )}
                                {event.data.payload?.connectionAge && (
                                  <p>Connection Age: {(event.data.payload.connectionAge / 1000 / 60 / 60).toFixed(1)} hours</p>
                                )}
                                {event.data.payload?.newSessionId && (
                                  <p>New Session: ...{event.data.payload.newSessionId.slice(-8)}</p>
                                )}
                                {event.data.payload?.activeOperations && event.data.payload.activeOperations.length > 0 && (
                                  <p>Active Ops: {event.data.payload.activeOperations.map(op => op.type).join(', ')}</p>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Session end with disconnection reason */}
                          {event.type === 'session_end' && (
                            <div className="mt-2 bg-red-50 rounded p-2 border border-red-200">
                              <p className="font-medium text-xs text-red-700 mb-1">📴 Session Ended</p>
                              <div className="text-xs text-red-700">
                                {event.data.disconnectionReason && (
                                  <p>Reason: {formatDisconnectionReason(event.data.disconnectionReason)}</p>
                                )}
                                {event.data.previousSessionId && (
                                  <p className="text-blue-600">Continued in session: ...{event.data.previousSessionId.slice(-8)}</p>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Software Update Events */}
                          {event.type === 'action' && event.data.actionType === 'update_initiated' && (
                            <div className="mt-2 bg-purple-50 rounded p-2 border border-purple-200">
                              <p className="font-medium text-xs text-purple-700 mb-1">📦 Software Update Initiated</p>
                              <div className="text-xs text-purple-700">
                                <p>Initiated by: {event.data.name || event.data.userId}</p>
                                <p>Component: {event.data.payload?.component}</p>
                                <p>Target Version: {event.data.payload?.targetVersion}</p>
                                {event.data.payload?.reason && (
                                  <p>Reason: {event.data.payload.reason}</p>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {event.type === 'action' && event.data.actionType === 'update_transfer_started' && (
                            <div className="mt-2 bg-purple-50 rounded p-2 border border-purple-200">
                              <p className="font-medium text-xs text-purple-700 mb-1">☁️ Update Transfer Started</p>
                              <div className="text-xs text-purple-700">
                                <p>Package Size: {(event.data.payload?.packageSize / 1024 / 1024).toFixed(2)} MB</p>
                                <p>Total Chunks: {event.data.payload?.chunks}</p>
                                <p>Transfer Session: {event.data.payload?.transferSessionId?.substring(0, 8)}...</p>
                              </div>
                            </div>
                          )}
                          
                          {event.type === 'action' && event.data.actionType === 'update_progress' && (
                            <div className="mt-2 bg-blue-50 rounded p-2 border border-blue-200">
                              <p className="font-medium text-xs text-blue-700 mb-1">📊 Update Progress</p>
                              <div className="text-xs text-blue-700">
                                <p>Progress: {event.data.payload?.progress}%</p>
                                <p>Component: {event.data.payload?.component}</p>
                                <p>Version: {event.data.payload?.version}</p>
                              </div>
                            </div>
                          )}
                          
                          {event.type === 'action' && event.data.actionType === 'update_completed' && (
                            <div className={`mt-2 rounded p-2 border ${
                              event.data.payload?.success 
                                ? 'bg-green-50 border-green-200' 
                                : 'bg-red-50 border-red-200'
                            }`}>
                              <p className={`font-medium text-xs mb-1 ${
                                event.data.payload?.success ? 'text-green-700' : 'text-red-700'
                              }`}>
                                {event.data.payload?.success ? '✅ Update Completed Successfully' : '❌ Update Failed'}
                              </p>
                              <div className={`text-xs ${
                                event.data.payload?.success ? 'text-green-700' : 'text-red-700'
                              }`}>
                                <p>Component: {event.data.payload?.component}</p>
                                <p>Version: {event.data.payload?.version}</p>
                                {event.data.payload?.duration && (
                                  <p>Duration: {(event.data.payload.duration / 1000).toFixed(1)}s</p>
                                )}
                                {event.data.payload?.error && (
                                  <p>Error: {event.data.payload.error}</p>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {event.type === 'action' && event.data.actionType === 'update_device_online' && (
                            <div className="mt-2 bg-green-50 rounded p-2 border border-green-200">
                              <p className="font-medium text-xs text-green-700 mb-1">🟢 Device Back Online</p>
                              <div className="text-xs text-green-700">
                                <p>Component: {event.data.payload?.component}</p>
                                <p>Previous: {event.data.payload?.previousVersion}</p>
                                <p>Current: {event.data.payload?.newVersion}</p>
                                {event.data.payload?.updateDuration && (
                                  <p>Total Time: {(event.data.payload.updateDuration / 1000).toFixed(1)}s</p>
                                )}
                              </div>
                            </div>
                          )}
                          {event.type === 'action' && event.data.actionType === 'update_api' && (
                            <div className="mt-2 bg-white rounded p-2 border border-gray-200">
                              <p className="font-medium text-xs text-gray-700 mb-1">Changes Made:</p>
                              {/* Display formatted changes from backend */}
                              {event.data.changesMade || event.data.changesFormatted || event.data.changesDescription ? (
                                <div className="text-xs text-gray-700">
                                  {event.data.changesMade || event.data.changesFormatted || event.data.changesDescription}
                                </div>
                              ) : event.data.changes && Array.isArray(event.data.changes) ? (
                                event.data.changes.map((change, idx) => (
                                  <div key={idx} className="flex items-center gap-2 text-xs">
                                    <span className="text-gray-600">{formatParameterName(change.parameter)}:</span>
                                    {change.oldValue !== null && change.oldValue !== undefined && (
                                      <>
                                        <span className="text-gray-500">{formatParameterValue(change.parameter, change.oldValue)}</span>
                                        <span className="text-gray-400">→</span>
                                      </>
                                    )}
                                    <span className="font-medium text-blue-600">{formatParameterValue(change.parameter, change.newValue)}</span>
                                    {change.success === false && (
                                      <span className="text-red-600 text-xs">(failed)</span>
                                    )}
                                  </div>
                                ))
                              ) : event.data.payload && event.data.payload.updates && Array.isArray(event.data.payload.updates) ? (
                                event.data.payload.updates.map((update, idx) => (
                                  <div key={idx} className="flex items-center gap-2 text-xs">
                                    <span className="text-gray-600">{formatParameterName(update.path)}:</span>
                                    <span className="font-medium text-blue-600">{formatParameterValue(update.path, update.value)}</span>
                                  </div>
                                ))
                              ) : (
                                <span className="text-xs text-gray-500">No change details available</span>
                              )}
                            </div>
                          )}
                          {/* Command Status and Retry Information */}
                          {event.data.commandId && (
                            <div className="mt-2 flex items-center gap-2">
                              <ActionStatusBadge 
                                status={event.data.ackStatus} 
                                retryCount={event.data.retryCount || 0}
                              />
                              {event.data.lastRetryAt && (
                                <span className="text-xs text-gray-500">
                                  Last retry: {format(new Date(event.data.lastRetryAt), 'HH:mm:ss')}
                                </span>
                              )}
                            </div>
                          )}
                          {event.data.errorMessage && (
                            <div className="mt-2 bg-red-50 rounded p-2 border border-red-200">
                              <p className="text-xs text-red-700">
                                <XCircleIcon className="w-3 h-3 inline mr-1" />
                                {event.data.errorMessage}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to format parameter names
const formatParameterName = (path) => {
  const parameterLabels = {
    moisture_setpoint: 'Moisture Setpoint',
    discharge_rate_setpoint: 'Discharge Rate Setpoint',
    drying_temperature_setpoint: 'Drying Temperature Setpoint',
    cooling_temperature_setpoint: 'Cooling Temperature Setpoint',
    mode: 'Device Mode',
    manual_control_enable: 'Manual Control',
    auto_control_enable: 'Auto Control',
    moisture_factor: 'Moisture Factor',
    drying_temperature: 'Drying Temperature',
    discharge_rate: 'Discharge Rate',
    // Add more parameter mappings as needed
  };
  
  return parameterLabels[path] || path.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to format disconnection reasons
const formatDisconnectionReason = (reason) => {
  if (!reason) return 'Unknown';
  
  // Stale connection timeout
  const staleMatch = reason.match(/stale_connection_timeout_(\d+)s/);
  if (staleMatch) {
    return `Stale connection (inactive for ${staleMatch[1]} seconds)`;
  }
  
  // WebSocket close codes
  const closeReasons = {
    'normal_closure': 'Normal closure',
    'going_away': 'Endpoint going away',
    'abnormal_closure': 'Abnormal closure',
    'policy_violation': 'Policy violation',
    'message_too_big': 'Message too big',
    'server_error': 'Server error',
    'health_refresh': 'Health-based connection refresh'
  };
  
  if (closeReasons[reason]) {
    return closeReasons[reason];
  }
  
  // WebSocket close code
  if (reason.startsWith('websocket_close_')) {
    const code = reason.replace('websocket_close_', '');
    return `WebSocket close (code ${code})`;
  }
  
  // WebSocket error
  if (reason.startsWith('websocket_error_')) {
    return `WebSocket error: ${reason.replace('websocket_error_', '')}`;
  }
  
  // Default: clean up underscores and capitalize
  return reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to format parameter values
const formatParameterValue = (path, value) => {
  // Format based on parameter type
  if (path.includes('mode')) {
    const modeLabels = {
      local_mode: 'Local Mode',
      manual_mode: 'Manual Mode',
      automatic_mode: 'Automatic Mode'
    };
    return modeLabels[value] || value;
  }
  
  if (path.includes('_enable') || path.includes('bool')) {
    return value ? 'Enabled' : 'Disabled';
  }
  
  // No unit here on purpose. These are past changes, and the unit the panel
  // was set to at the time was never recorded, so any letter we print is a
  // guess. The bare number is the only honest thing to show.
  if (path.includes('temperature')) {
    return `${value}`;
  }
  
  if (path.includes('moisture')) {
    return `${value}%`;
  }
  
  if (path.includes('rate')) {
    return `${value} BPH`;
  }
  
  return value;
};

// User Action Card Component - Shows individual user with expandable actions
// isExpanded and onToggleExpand are lifted to parent to preserve state across re-fetches
const UserActionCard = ({ user, deviceId, dateRange, onActionsFetched, isExpanded, onToggleExpand, showViewingSessions }) => {
  const [actions, setActions] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);
  const [lastFetchedRange, setLastFetchedRange] = useState(null);
  // Pagination for actions
  const [actionsPage, setActionsPage] = useState(1);
  const actionsPerPage = 10;

  // Watch sessions stay hidden unless the panel-level toggle is on
  const visibleActions = showViewingSessions
    ? actions
    : actions.filter(a => a.type !== 'watch_session');
  const hiddenCount = actions.length - visibleActions.length;

  // Back to page 1 when the filter changes the list length
  useEffect(() => {
    setActionsPage(1);
  }, [showViewingSessions]);

  const fetchUserActions = useCallback(async (forceRefresh = false) => {
    // Build date range key for comparison
    const rangeKey = dateRange ? `${dateRange.startDate?.toISOString()}-${dateRange.endDate?.toISOString()}` : 'all';

    // Skip if already loaded with same date range (unless force refresh)
    if (!forceRefresh && lastFetchedRange === rangeKey && actions.length > 0) {
      return;
    }

    setLoadingActions(true);
    try {
      // Build query params based on date range
      let url = `/session-activity/${deviceId}/users/${user.userId}/actions?limit=100`;
      if (dateRange && dateRange.startDate && dateRange.endDate) {
        url += `&startDate=${dateRange.startDate.toISOString()}&endDate=${dateRange.endDate.toISOString()}`;
      }
      // If no date range, fetch all actions (no date filter)

      const response = await axiosInstance.get(url);
      setActions(response.data.data.actions || []);
      setActionsPage(1); // Reset to first page when new actions loaded
      setLastFetchedRange(rangeKey);
      if (onActionsFetched) {
        onActionsFetched(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching user actions:', err);
    } finally {
      setLoadingActions(false);
    }
  }, [deviceId, user.userId, dateRange, onActionsFetched, lastFetchedRange, actions.length]);

  // Refetch actions when date range changes (only if expanded)
  useEffect(() => {
    if (isExpanded && dateRange) {
      const rangeKey = `${dateRange.startDate?.toISOString()}-${dateRange.endDate?.toISOString()}`;
      if (lastFetchedRange !== rangeKey) {
        fetchUserActions(true);
      }
    }
  }, [dateRange, isExpanded, lastFetchedRange, fetchUserActions]);

  const handleExpand = () => {
    if (!isExpanded) {
      fetchUserActions();
    }
    onToggleExpand(user.userId);
  };

  // Helper to translate action types to user-friendly text
  const translateAction = (type) => {
    const translations = {
      'update_api': 'Changed Settings',
      'reboot_command': 'Restarted Device',
      'terminal_session': 'Remote Access',
      'connection_refresh': 'Refreshed Connection',
      'session_refresh': 'Session Refresh',
      'stream_started': 'Started Watching',
      'stream_stopped': 'Stopped Watching',
      'update_initiated': 'Started Update',
      'update_completed': 'Finished Update',
      'watch_session': 'Watched Device'
    };
    return translations[type] || type?.replace(/_/g, ' ');
  };

  // Helper to get action icon and color
  const getActionStyle = (type) => {
    const styles = {
      'update_api': { bg: 'bg-blue-100', text: 'text-blue-700', icon: '⚙️' },
      'reboot_command': { bg: 'bg-red-100', text: 'text-red-700', icon: '🔄' },
      'terminal_session': { bg: 'bg-purple-100', text: 'text-purple-700', icon: '💻' },
      'watch_session': { bg: 'bg-green-100', text: 'text-green-700', icon: '👁️' },
      'connection_refresh': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: '🔃' },
      'stream_started': { bg: 'bg-green-100', text: 'text-green-700', icon: '▶️' },
      'stream_stopped': { bg: 'bg-gray-100', text: 'text-gray-700', icon: '⏹️' }
    };
    return styles[type] || { bg: 'bg-gray-100', text: 'text-gray-700', icon: '📋' };
  };

  // Helper to translate parameter names
  const translateParam = (param) => {
    const translations = {
      'moisture_setpoint': 'Target Moisture',
      'discharge_rate_setpoint': 'Discharge Speed',
      'drying_temperature_setpoint': 'Drying Temp',
      'cooling_temperature_setpoint': 'Cooling Temp',
      'mode': 'Operating Mode',
      'manual_control_enable': 'Manual Control',
      'auto_control_enable': 'Auto Control'
    };
    return translations[param] || param?.replace(/_/g, ' ');
  };

  // Format role for display. 'superadmin' still appears in rows logged
  // before the role was renamed to developer.
  const formatRole = (role) => {
    const roles = {
      'developer': 'Developer',
      'admin': 'Administrator',
      'employee': 'Employee',
      'technician': 'Technician',
      'superadmin': 'Developer',
      'operator': 'Operator',
      'user': 'User'
    };
    return roles[role] || role || 'User';
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border-2 overflow-hidden transition-all ${
      user.isWatching ? 'border-green-300 ring-2 ring-green-100' : 'border-gray-200'
    }`}>
      {/* User Header - Always visible */}
      <div
        className={`p-4 cursor-pointer transition-colors ${
          user.isWatching ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-gray-50'
        }`}
        onClick={handleExpand}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Avatar with online indicator */}
            <div className="relative">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                user.isWatching ? 'bg-green-100' : 'bg-blue-100'
              }`}>
                <span className={`text-xl font-bold ${
                  user.isWatching ? 'text-green-700' : 'text-blue-700'
                }`}>
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              {/* Online indicator dot */}
              {user.isWatching && (
                <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                </span>
              )}
            </div>

            {/* User Info */}
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-gray-900">{user.name || 'Unknown User'}</p>
                {/* Account type badge */}
                {user.accountType === 'employee' && (
                  <span className="px-1.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                    Staff
                  </span>
                )}
                {user.accountType === 'user' && (
                  <span className="px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded">
                    Customer
                  </span>
                )}
                {user.isWatching && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-green-500 text-white rounded-full animate-pulse">
                    LIVE
                  </span>
                )}
              </div>
              {/* Email */}
              {user.email && (
                <p className="text-sm text-blue-600">{user.email}</p>
              )}
              <p className="text-sm text-gray-500">{formatRole(user.role)}</p>
              {/* Watching info - Enhanced */}
              {user.isWatching && (
                <div className="mt-1 space-y-0.5">
                  {user.watchingSince && (
                    <p className="text-xs text-green-600 font-medium">
                      🟢 Watching for {formatDistanceToNow(new Date(user.watchingSince))}
                    </p>
                  )}
                  {user.watchingTopics && user.watchingTopics.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="text-xs text-gray-500">Topics:</span>
                      {user.watchingTopics.map((topic, idx) => (
                        <span key={idx} className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                          {topic}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* No activity indicator */}
              {!user.isWatching && !user.hasActivityInPeriod && (
                <p className="text-xs text-orange-500 mt-0.5">
                  No activity in selected period
                </p>
              )}
            </div>
          </div>

          {/* Stats & Arrow */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 text-sm">
              {/* Currently Watching Status - Most prominent */}
              {user.isWatching && (
                <div className="text-center px-3 py-1 bg-green-100 border border-green-300 rounded-lg">
                  <div className="flex items-center gap-1.5 justify-center">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                    <p className="font-bold text-green-700 text-xs">LIVE</p>
                  </div>
                  <p className="text-xs text-green-600">
                    {user.watchingSince ? formatDistanceToNow(new Date(user.watchingSince)) : 'Now'}
                  </p>
                </div>
              )}
              {/* Actions */}
              <div className="text-center px-3 py-1 bg-gray-100 rounded-lg">
                <p className="font-bold text-gray-900">{user.actionCount || 0}</p>
                <p className="text-xs text-gray-500">Actions</p>
              </div>
              {/* Sessions */}
              <div className="text-center px-3 py-1 bg-gray-100 rounded-lg">
                <p className="font-bold text-gray-900">{user.sessionCount || 0}</p>
                <p className="text-xs text-gray-500">Sessions</p>
              </div>
              {/* Last Activity - Hide if currently watching */}
              {!user.isWatching && (
                <div className="text-center min-w-[100px]">
                  <p className="text-xs text-gray-500">Last activity</p>
                  <p className="text-sm font-medium text-gray-700">
                    {user.lastSeen ? formatDistanceToNow(new Date(user.lastSeen), { addSuffix: true }).replace('about ', '') : 'N/A'}
                  </p>
                  {user.lastSeen && (
                    <p className="text-xs text-gray-400">
                      {format(new Date(user.lastSeen), 'MMM d, h:mm a')}
                    </p>
                  )}
                </div>
              )}
            </div>

            {isExpanded ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* Expandable Actions Section */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-gray-200 overflow-hidden"
          >
            <div className="p-4 bg-gray-50">
              {loadingActions ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : visibleActions.length === 0 ? (
                <div className="text-center py-8">
                  <CommandLineIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No actions recorded</p>
                  {hiddenCount > 0 && (
                    <p className="text-xs text-gray-400 mt-1">
                      {hiddenCount} viewing session{hiddenCount !== 1 ? 's' : ''} hidden
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Header with count and pagination info */}
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-gray-700">
                      Actions ({visibleActions.length})
                      {hiddenCount > 0 && (
                        <span className="ml-2 text-xs font-normal text-gray-400">
                          {hiddenCount} viewing session{hiddenCount !== 1 ? 's' : ''} hidden
                        </span>
                      )}
                    </p>
                    {visibleActions.length > actionsPerPage && (
                      <p className="text-xs text-gray-500">
                        Page {actionsPage} of {Math.ceil(visibleActions.length / actionsPerPage)}
                      </p>
                    )}
                  </div>
                  {visibleActions.slice((actionsPage - 1) * actionsPerPage, actionsPage * actionsPerPage).map((action, idx) => (
                    <div
                      key={idx}
                      className={`rounded-lg p-3 border ${
                        action.type === 'reboot_command' ? 'bg-red-50 border-red-200' :
                        action.type === 'terminal_session' ? 'bg-purple-50 border-purple-200' :
                        action.type === 'update_api' ? 'bg-blue-50 border-blue-200' :
                        action.type === 'watch_session' ? (action.watchDetails?.isActive ? 'bg-green-50 border-green-300' : 'bg-emerald-50 border-emerald-200') :
                        'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {/* Action Icon */}
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            action.type === 'reboot_command' ? 'bg-red-100' :
                            action.type === 'terminal_session' ? 'bg-purple-100' :
                            action.type === 'update_api' ? 'bg-blue-100' :
                            action.type === 'watch_session' ? 'bg-green-100' :
                            'bg-gray-100'
                          }`}>
                            {action.type === 'reboot_command' ? (
                              <ArrowPathIcon className="w-4 h-4 text-red-600" />
                            ) : action.type === 'terminal_session' ? (
                              <ComputerDesktopIcon className="w-4 h-4 text-purple-600" />
                            ) : action.type === 'update_api' ? (
                              <BoltIcon className="w-4 h-4 text-blue-600" />
                            ) : action.type === 'watch_session' ? (
                              <EyeIcon className="w-4 h-4 text-green-600" />
                            ) : (
                              <CommandLineIcon className="w-4 h-4 text-gray-600" />
                            )}
                          </div>

                          {/* Action Details */}
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium ${
                              action.type === 'reboot_command' ? 'text-red-700' :
                              action.type === 'terminal_session' ? 'text-purple-700' :
                              action.type === 'update_api' ? 'text-blue-700' :
                              action.type === 'watch_session' ? 'text-green-700' :
                              'text-gray-700'
                            }`}>
                              {translateAction(action.type)}
                            </p>

                            {/* Show changes for update_api */}
                            {action.type === 'update_api' && action.changes && action.changes.length > 0 && (
                              <div className="mt-1 space-y-0.5">
                                {action.changes.map((change, cIdx) => (
                                  <p key={cIdx} className="text-sm text-gray-600">
                                    {translateParam(change.parameter)}:
                                    {change.oldValue !== null && change.oldValue !== undefined && (
                                      <span className="text-gray-400"> {change.oldValue} → </span>
                                    )}
                                    <span className="font-medium text-blue-600">{change.newValue}</span>
                                  </p>
                                ))}
                              </div>
                            )}

                            {/* Terminal session details */}
                            {action.type === 'terminal_session' && action.payload && (
                              <p className="text-sm text-gray-500 mt-1">
                                {action.payload.action === 'opened' ? 'Opened' : 'Closed'} {action.payload.terminalType || 'Terminal'}
                                {action.payload.durationFormatted && ` (${action.payload.durationFormatted})`}
                              </p>
                            )}

                            {/* Reboot details */}
                            {action.type === 'reboot_command' && action.payload && (
                              <p className="text-sm text-gray-500 mt-1">
                                {action.payload.reason && `Reason: ${action.payload.reason}`}
                              </p>
                            )}

                            {/* Watch session details */}
                            {action.type === 'watch_session' && action.watchDetails && (
                              <div className="mt-1 space-y-1">
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="text-gray-600">
                                    {action.watchDetails.isActive ? 'Watching' : 'Watched'} for <span className="font-medium text-gray-800">{action.watchDetails.durationFormatted || '< 1 min'}</span>
                                  </span>
                                </div>
                                {action.watchDetails.topics && action.watchDetails.topics.length > 0 && (
                                  <div className="flex items-center gap-1 flex-wrap">
                                    <span className="text-xs text-gray-500">Topics:</span>
                                    {action.watchDetails.topics.map((topic, tIdx) => (
                                      <span key={tIdx} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                        {topic}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {action.watchDetails.disconnectionReason && action.watchDetails.disconnectionReason !== 'normal' && (
                                  <p className="text-xs text-orange-600">
                                    Disconnected: {action.watchDetails.disconnectionReason.replace(/_/g, ' ')}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Status badge for commands - moved here */}
                            {action.commandId && action.ackStatus && (
                              <div className="mt-1.5">
                                <ActionStatusBadge status={action.ackStatus} retryCount={action.retryCount || 0} />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Timestamp - Enhanced */}
                        <div className="text-right flex-shrink-0 ml-3">
                          <p className="text-xs text-gray-500 font-medium">
                            {formatDistanceToNow(new Date(action.timestamp), { addSuffix: true }).replace('about ', '')}
                          </p>
                          <p className="text-sm font-medium text-gray-700">
                            {format(new Date(action.timestamp), 'MMM d')}
                          </p>
                          <p className="text-xs text-gray-400">
                            {format(new Date(action.timestamp), 'h:mm:ss a')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Pagination Controls */}
                  {visibleActions.length > actionsPerPage && (
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200 mt-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActionsPage(p => Math.max(1, p - 1));
                        }}
                        disabled={actionsPage === 1}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronUpIcon className="w-4 h-4 rotate-[-90deg]" />
                        Previous
                      </button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.ceil(visibleActions.length / actionsPerPage) }, (_, i) => i + 1)
                          .filter(page => {
                            // Show first, last, current, and adjacent pages
                            const totalPages = Math.ceil(visibleActions.length / actionsPerPage);
                            return page === 1 || page === totalPages || Math.abs(page - actionsPage) <= 1;
                          })
                          .map((page, idx, arr) => (
                            <span key={page} className="flex items-center">
                              {idx > 0 && arr[idx - 1] !== page - 1 && (
                                <span className="px-1 text-gray-400">...</span>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActionsPage(page);
                                }}
                                className={`w-8 h-8 text-sm rounded-lg ${
                                  actionsPage === page
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white border border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {page}
                              </button>
                            </span>
                          ))}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActionsPage(p => Math.min(Math.ceil(visibleActions.length / actionsPerPage), p + 1));
                        }}
                        disabled={actionsPage >= Math.ceil(visibleActions.length / actionsPerPage)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                        <ChevronDownIcon className="w-4 h-4 rotate-[-90deg]" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SessionActivity = ({ deviceId }) => {
  const [activeTab, setActiveTab] = useState('users'); // Default to 'users' tab
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [totalUsersCount, setTotalUsersCount] = useState(0); // Total users ever (not filtered)
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [filters, setFilters] = useState({
    limit: 20,
    skip: 0
  });
  const [stats, setStats] = useState(null);
  const [liveRefreshStatus, setLiveRefreshStatus] = useState(null);
  // Time range states for sessions and users
  const [sessionsTimeRange, setSessionsTimeRange] = useState('7d');
  const [usersTimeRange, setUsersTimeRange] = useState('24h'); // Default to 24 hours
  const [sessionsCustomRange, setSessionsCustomRange] = useState({ start: null, end: null });
  const [usersCustomRange, setUsersCustomRange] = useState({ start: null, end: null });
  // Track expanded user cards - lifted up to preserve state across re-fetches
  const [expandedUserIds, setExpandedUserIds] = useState(new Set());
  // Watch sessions clutter the action lists, so they stay hidden by default
  const [showViewingSessions, setShowViewingSessions] = useState(false);

  // Toggle expanded state for a user card
  const toggleUserExpanded = useCallback((userId) => {
    setExpandedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  // Get WebSocket context
  const { topicData } = useWebSocket();

  // Helper to calculate date range from time range selection
  const getDateRange = (timeRange, customRange) => {
    const now = new Date();
    let startDate, endDate = now;

    switch (timeRange) {
      case 'all':
        // No date filter - return null values to indicate all time
        return { startDate: null, endDate: null };
      case '24h':
        startDate = subHours(now, 24);
        break;
      case '48h':
        startDate = subHours(now, 48);
        break;
      case '7d':
        startDate = subDays(now, 7);
        break;
      case '14d':
        startDate = subDays(now, 14);
        break;
      case '30d':
        startDate = subDays(now, 30);
        break;
      case '90d':
        startDate = subDays(now, 90);
        break;
      case 'custom':
        if (customRange?.start && customRange?.end) {
          startDate = customRange.start;
          endDate = customRange.end;
        } else {
          startDate = subDays(now, 7);
        }
        break;
      default:
        startDate = subDays(now, 7);
    }

    return { startDate, endDate };
  };

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);

      // Calculate date range
      const { startDate, endDate } = getDateRange(sessionsTimeRange, sessionsCustomRange);

      const params = new URLSearchParams({
        limit: filters.limit,
        skip: filters.skip,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });

      const response = await axiosInstance.get(
        `/session-activity/${deviceId}?${params}`
      );

      setSessions(response.data.data.sessions);
      setStats(response.data.data.stats);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch session activity');
      console.error('Error fetching session activity:', err);
    } finally {
      setLoading(false);
    }
  }, [deviceId, filters, sessionsTimeRange, sessionsCustomRange]);

  const fetchSessionDetails = async (session) => {
    try {
      const response = await axiosInstance.get(
        `/session-activity/${deviceId}/session/${session.sessionId}`
      );

      setSelectedSession(response.data.data.session);
      setTimeline(response.data.data.timeline);
    } catch (err) {
      console.error('Error fetching session details:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch session details');
    }
  };

  // Fetch ALL users who have ever interacted with this device
  // Date filter affects the stats shown (sessionCount, actionCount, lastSeen)
  // Fetch total users count (no date filter) - for the chip display
  const fetchTotalUsersCount = useCallback(async () => {
    try {
      // No date filter - get ALL users ever
      const response = await axiosInstance.get(`/session-activity/${deviceId}/users`);
      setTotalUsersCount(response.data.data.users?.length || 0);
    } catch (err) {
      console.error('Error fetching total users count:', err);
    }
  }, [deviceId]);

  // Fetch users with date filter for display
  const fetchUsers = useCallback(async () => {
    try {
      setUsersLoading(true);

      // Get date range for filtering stats
      const { startDate, endDate } = getDateRange(usersTimeRange, usersCustomRange);

      // Build URL with date filter for stats
      let url = `/session-activity/${deviceId}/users`;
      if (startDate && endDate) {
        url += `?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
      }

      const response = await axiosInstance.get(url);
      const fetchedUsers = response.data.data.users || [];
      setUsers(fetchedUsers);

      // Also update total count if this is an unfiltered request
      if (!startDate && !endDate) {
        setTotalUsersCount(fetchedUsers.length);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setUsersLoading(false);
    }
  }, [deviceId, usersTimeRange, usersCustomRange]);

  // Fetch total users count on mount (for chip)
  useEffect(() => {
    fetchTotalUsersCount();
  }, [deviceId]); // Only fetch on mount or device change

  // Fetch users when tab changes to users OR when time period changes
  // Combined into single useEffect to prevent multiple fetches
  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, deviceId, usersTimeRange, usersCustomRange]); // Don't include fetchUsers to prevent loop
  
  // Memoize filtered user lists to prevent unnecessary re-renders
  const customerUsers = useMemo(() => users.filter(u => u.accountType === 'user'), [users]);
  const employeeUsers = useMemo(() => users.filter(u => u.accountType === 'employee'), [users]);
  const otherUsers = useMemo(() => users.filter(u => u.accountType !== 'employee' && u.accountType !== 'user'), [users]);

  // Memoize date range to prevent unnecessary re-renders of UserActionCard
  const currentDateRange = useMemo(() => getDateRange(usersTimeRange, usersCustomRange), [usersTimeRange, usersCustomRange]);

  // Monitor for refresh status updates
  const refreshStatus = useMemo(() => {
    const refreshKey = `${deviceId}:refresh_status`;
    return topicData?.get(refreshKey)?.data;
  }, [topicData, deviceId]);
  
  // Update live refresh status when WebSocket message arrives
  useEffect(() => {
    if (refreshStatus) {
      setLiveRefreshStatus(refreshStatus);
      
      // Auto-refresh sessions after a refresh completes
      if (refreshStatus.status === 'refreshing' || refreshStatus.status === 'completed') {
        // Wait a moment for the new session to be created
        const timer = setTimeout(() => {
          fetchSessions();
        }, 2000);
        return () => clearTimeout(timer);
      }
      
      // Clear refresh status after 10 seconds
      const clearTimer = setTimeout(() => {
        setLiveRefreshStatus(null);
      }, 10000);
      return () => clearTimeout(clearTimer);
    }
  }, [refreshStatus, fetchSessions]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const refreshData = () => {
    fetchSessions();
  };

  if (loading && sessions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={refreshData}
          className="text-blue-600 hover:text-blue-700"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Live Refresh Status Indicator */}
      {liveRefreshStatus && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-3">
          <ArrowPathIcon className="w-5 h-5 text-yellow-600 animate-spin" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800">
              Connection Refresh in Progress
            </p>
            <p className="text-xs text-yellow-700 mt-1">
              Status: {liveRefreshStatus.status} • Reason: {liveRefreshStatus.reason}
              {liveRefreshStatus.activeOperations && liveRefreshStatus.activeOperations.length > 0 && (
                <span> • Waiting for {liveRefreshStatus.activeOperations.length} operations</span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('sessions')}
            className={`px-4 py-2 rounded-md font-medium text-sm transition-all ${
              activeTab === 'sessions'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Sessions
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-md font-medium text-sm transition-all ${
              activeTab === 'users'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center gap-2">
              <UserGroupIcon className="w-4 h-4" />
              Users
              {totalUsersCount > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                  {totalUsersCount}
                </span>
              )}
            </span>
          </button>
        </div>

        <button
          onClick={activeTab === 'sessions' ? refreshData : fetchUsers}
          disabled={loading || usersLoading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <ArrowPathIcon className={`w-4 h-4 ${(loading || usersLoading) ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Sessions Tab Content */}
      {activeTab === 'sessions' && (
        <>
          {/* Time period filter for Sessions */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <CalendarIcon className="w-5 h-5 text-gray-500" />
              <select
                value={sessionsTimeRange}
                onChange={(e) => setSessionsTimeRange(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="24h">Last 24 Hours</option>
                <option value="48h">Last 48 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="14d">Last 14 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Stats summary */}
            {stats && (
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>{stats.totalSessions} session{stats.totalSessions !== 1 ? 's' : ''}</span>
                <span className="text-green-600">{stats.activeSessions} active</span>
                <span>{stats.uniqueUsers} user{stats.uniqueUsers !== 1 ? 's' : ''}</span>
                <span>{stats.totalActions} action{stats.totalActions !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>

          {/* Custom Date Range for Sessions */}
          {sessionsTimeRange === 'custom' && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 whitespace-nowrap">From:</label>
                  <input
                    type="datetime-local"
                    value={sessionsCustomRange.start ? format(sessionsCustomRange.start, "yyyy-MM-dd'T'HH:mm") : ''}
                    onChange={(e) => {
                      const newDate = new Date(e.target.value);
                      if (!isNaN(newDate.getTime())) {
                        setSessionsCustomRange(prev => ({ ...prev, start: newDate }));
                      }
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 whitespace-nowrap">To:</label>
                  <input
                    type="datetime-local"
                    value={sessionsCustomRange.end ? format(sessionsCustomRange.end, "yyyy-MM-dd'T'HH:mm") : ''}
                    onChange={(e) => {
                      const newDate = new Date(e.target.value);
                      if (!isNaN(newDate.getTime())) {
                        setSessionsCustomRange(prev => ({ ...prev, end: newDate }));
                      }
                    }}
                    min={sessionsCustomRange.start ? format(sessionsCustomRange.start, "yyyy-MM-dd'T'HH:mm") : undefined}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <button
                  onClick={fetchSessions}
                  disabled={!sessionsCustomRange.start || !sessionsCustomRange.end}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply
                </button>
              </div>
            </div>
          )}

          {/* Session List */}
          <div className="space-y-3">
            {sessions.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <ChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No sessions found</p>
              </div>
            ) : (
              sessions.map((session) => (
                <SessionCard
                  key={session.sessionId}
                  session={session}
                  onViewDetails={fetchSessionDetails}
                />
              ))
            )}
          </div>

          {/* Pagination */}
          {sessions.length > 0 && (
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <button
                onClick={() => setFilters({ ...filters, skip: Math.max(0, filters.skip - filters.limit) })}
                disabled={filters.skip === 0}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <p className="text-sm text-gray-600">
                Showing {filters.skip + 1} - {filters.skip + sessions.length} of {stats?.totalSessions || 0}
              </p>

              <button
                onClick={() => setFilters({ ...filters, skip: filters.skip + filters.limit })}
                disabled={filters.skip + sessions.length >= (stats?.totalSessions || 0)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Users Tab Content */}
      {activeTab === 'users' && (
        <>
          {/* Time period filter - affects actions shown when user is expanded */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <CalendarIcon className="w-5 h-5 text-gray-500" />
              <div className="flex flex-col">
                <select
                  value={usersTimeRange}
                  onChange={(e) => setUsersTimeRange(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Time</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="48h">Last 48 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="14d">Last 14 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                  <option value="custom">Custom Range</option>
                </select>
                <span className="text-xs text-gray-400 mt-1">Filter actions by time period</span>
              </div>
              <label className="flex items-center gap-2 ml-2 text-sm text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showViewingSessions}
                  onChange={(e) => setShowViewingSessions(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Show viewing sessions
              </label>
            </div>

            {/* Currently watching indicator */}
            {users.filter(u => u.isWatching).length > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                <span className="text-sm font-medium text-green-700">
                  {users.filter(u => u.isWatching).length} watching now
                </span>
              </div>
            )}
          </div>

          {/* Custom Date Range for Users */}
          {usersTimeRange === 'custom' && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 whitespace-nowrap">From:</label>
                  <input
                    type="datetime-local"
                    value={usersCustomRange.start ? format(usersCustomRange.start, "yyyy-MM-dd'T'HH:mm") : ''}
                    onChange={(e) => {
                      const newDate = new Date(e.target.value);
                      if (!isNaN(newDate.getTime())) {
                        setUsersCustomRange(prev => ({ ...prev, start: newDate }));
                      }
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 whitespace-nowrap">To:</label>
                  <input
                    type="datetime-local"
                    value={usersCustomRange.end ? format(usersCustomRange.end, "yyyy-MM-dd'T'HH:mm") : ''}
                    onChange={(e) => {
                      const newDate = new Date(e.target.value);
                      if (!isNaN(newDate.getTime())) {
                        setUsersCustomRange(prev => ({ ...prev, end: newDate }));
                      }
                    }}
                    min={usersCustomRange.start ? format(usersCustomRange.start, "yyyy-MM-dd'T'HH:mm") : undefined}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <button
                  onClick={fetchUsers}
                  disabled={!usersCustomRange.start || !usersCustomRange.end}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply
                </button>
              </div>
            </div>
          )}

          {/* Users List */}
          {usersLoading && users.length === 0 ? (
            // Only show full loading spinner on initial load (no users yet)
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No users have interacted with this device</p>
            </div>
          ) : (
            <div className="space-y-6 relative">
              {/* Subtle loading overlay when refreshing - keeps cards visible */}
              {usersLoading && (
                <div className="absolute inset-0 bg-white/50 z-10 flex items-start justify-center pt-8 rounded-lg">
                  <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-lg border">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-sm text-gray-600">Updating...</span>
                  </div>
                </div>
              )}
              {/* Summary */}
              <p className="text-sm text-gray-500">
                {users.length} {users.length === 1 ? 'user has' : 'users have'} interacted with this device.
                Click on a user to see their actions{usersTimeRange !== 'all' ? ` (filtered by selected time period)` : ''}.
              </p>

              {/* Customers Section - FIRST */}
              {customerUsers.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b border-amber-200">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                        <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-amber-800">
                        Customers / Users
                        <span className="ml-2 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
                          {customerUsers.length}
                        </span>
                      </h3>
                    </div>
                    {/* Watching indicator for customers */}
                    {customerUsers.filter(u => u.isWatching).length > 0 && (
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-green-100 rounded-full">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        <span className="text-xs font-medium text-green-700">
                          {customerUsers.filter(u => u.isWatching).length} watching
                        </span>
                      </div>
                    )}
                  </div>
                  {customerUsers.map((user) => (
                    <UserActionCard
                      key={user.userId}
                      user={user}
                      deviceId={deviceId}
                      dateRange={currentDateRange}
                      showViewingSessions={showViewingSessions}
                      isExpanded={expandedUserIds.has(user.userId)}
                      onToggleExpand={toggleUserExpanded}
                    />
                  ))}
                </div>
              )}

              {/* Employees Section - SECOND */}
              {employeeUsers.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b border-purple-200">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-purple-800">
                        Employees / Staff
                        <span className="ml-2 px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                          {employeeUsers.length}
                        </span>
                      </h3>
                    </div>
                    {/* Watching indicator for employees */}
                    {employeeUsers.filter(u => u.isWatching).length > 0 && (
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-green-100 rounded-full">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        <span className="text-xs font-medium text-green-700">
                          {employeeUsers.filter(u => u.isWatching).length} watching
                        </span>
                      </div>
                    )}
                  </div>
                  {employeeUsers.map((user) => (
                    <UserActionCard
                      key={user.userId}
                      user={user}
                      deviceId={deviceId}
                      dateRange={currentDateRange}
                      showViewingSessions={showViewingSessions}
                      isExpanded={expandedUserIds.has(user.userId)}
                      onToggleExpand={toggleUserExpanded}
                    />
                  ))}
                </div>
              )}

              {/* Unknown/Other Section (if any) - LAST */}
              {otherUsers.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <UserGroupIcon className="w-4 h-4 text-gray-600" />
                    </div>
                    <h3 className="font-semibold text-gray-700">
                      Other
                      <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                        {otherUsers.length}
                      </span>
                    </h3>
                  </div>
                  {otherUsers.map((user) => (
                    <UserActionCard
                      key={user.userId}
                      user={user}
                      deviceId={deviceId}
                      dateRange={currentDateRange}
                      showViewingSessions={showViewingSessions}
                      isExpanded={expandedUserIds.has(user.userId)}
                      onToggleExpand={toggleUserExpanded}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Timeline Modal */}
      {selectedSession && timeline && (
        <SessionTimeline
          session={selectedSession}
          timeline={timeline}
          onClose={() => {
            setSelectedSession(null);
            setTimeline(null);
          }}
        />
      )}
    </div>
  );
};

export default SessionActivity;